/**
 * Messages hook — TanStack Query infinite query + Supabase Realtime.
 *
 * Cursor-based pagination (newest first). Realtime prepends new messages
 * from other users. Optimistic insert on send, deduped against the
 * realtime echo via `clientMessageId` so the same message never renders
 * twice.
 */
import { useCallback, useEffect } from 'react'
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import * as Crypto from 'expo-crypto'
import { supabase } from '../supabase'
import { messagingService } from '../services/messaging.service'
import { filterMessage } from '../utils/message-filter'
import { useAuth } from './use-auth'
import { conversationKeys } from './use-conversations'
import type {
  MessageItem,
  MessagePage,
  MessageReplyPreview,
  MessageSearchResult,
  SendMessageInput,
} from '../types/messaging.types'

// ─── Query Keys ─────────────────────────────────────────────────────────────

const messageKeys = {
  all: ['messages'] as const,
  list: (conversationId: string) => [...messageKeys.all, conversationId] as const,
}

type MessagesQueryData = { pages: MessagePage[]; pageParams: unknown[] }

function toReplyPreview(message: MessageItem): MessageReplyPreview {
  return {
    id: message.id,
    senderId: message.senderId,
    content: message.content,
    messageType: message.messageType,
    isDeleted: message.isDeleted,
  }
}

function findCachedReplyPreview(
  cache: MessagesQueryData | undefined,
  replyToId: string | null,
): MessageReplyPreview | null {
  if (!cache || !replyToId) return null
  for (const page of cache.pages) {
    const message = page.messages.find((candidate) => candidate.id === replyToId)
    if (message) return toReplyPreview(message)
  }
  return null
}

function hydrateReplyPreview(
  cache: MessagesQueryData | undefined,
  messageId: string,
  replyTo: MessageReplyPreview,
): MessagesQueryData | undefined {
  if (!cache) return cache
  return {
    ...cache,
    pages: cache.pages.map((page) => ({
      ...page,
      messages: page.messages.map((message) =>
        message.id === messageId ? { ...message, replyTo } : message,
      ),
    })),
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Replace an optimistic (or duplicate) message in the cache by
 * `clientMessageId` match. Falls back to prepending if no match — i.e.,
 * the message is from the other participant.
 *
 * We hydrate the realtime payload from the existing cache when possible
 * (sender display_name + avatar) instead of stubbing them as `null` —
 * stub names produce empty avatars on the next render until the next
 * full refetch invalidates the query.
 */
function reconcileMessage(
  cache: MessagesQueryData | undefined,
  incoming: MessageItem,
  source: 'response' | 'realtime' = 'response',
): MessagesQueryData | undefined {
  if (!cache) return cache
  const firstPage = cache.pages[0]
  if (!firstPage) return cache

  // Both the INSERT response and its realtime echo can arrive first. Match
  // across every loaded page so either order upgrades one canonical row.
  for (const [pageIndex, page] of cache.pages.entries()) {
    const existingIdx = page.messages.findIndex(
      (m) =>
        m.id === incoming.id ||
        (!!incoming.clientMessageId && m.clientMessageId === incoming.clientMessageId),
    )
    if (existingIdx !== -1) {
      const existing = page.messages[existingIdx]!
      const upgraded: MessageItem = {
        ...existing,
        ...incoming,
        senderDisplayName: incoming.senderDisplayName ?? existing.senderDisplayName,
        senderAvatarUrl: incoming.senderAvatarUrl ?? existing.senderAvatarUrl,
        // The realtime row contains the parent ID but not the nested preview.
        // Never replace an optimistic reply chip with an empty one just
        // because the socket won the race against the INSERT response.
        replyTo: incoming.replyTo ?? existing.replyTo,
        // Realtime has storage paths; the service response has signed URLs.
        mediaUrl:
          source === 'realtime' ? (existing.mediaUrl ?? incoming.mediaUrl) : incoming.mediaUrl,
      }
      const nextMessages = [...page.messages]
      nextMessages[existingIdx] = upgraded
      const nextPages = [...cache.pages]
      nextPages[pageIndex] = { ...page, messages: nextMessages }
      return {
        ...cache,
        pages: nextPages,
      }
    }
  }

  // No match — this is a new message from the other participant. Prepend.
  return {
    ...cache,
    pages: [{ ...firstPage, messages: [incoming, ...firstPage.messages] }, ...cache.pages.slice(1)],
  }
}

/**
 * Zero the unread badge for one conversation everywhere it is cached.
 *
 * `messagingService.markAsRead` clears the counter server-side, but the
 * conversation list is a separate cache that `use-conversations` has
 * already bumped by +1 for the arriving realtime row. Invalidating only
 * the tab total left the per-row badge lit on a message the user was
 * literally reading, until the list happened to refetch.
 *
 * Call this AFTER `markAsRead` resolves — by then the conversation-list
 * subscriber has applied its +1, so the reset is the last write to win.
 */
function clearConversationUnread(
  queryClient: QueryClient,
  userId: string,
  conversationId: string,
): void {
  queryClient.invalidateQueries({ queryKey: conversationKeys.unreadTotal(userId) })
  queryClient.setQueriesData<{ unreadCount: number; id: string }[]>(
    { queryKey: conversationKeys.list(userId) },
    (old) => {
      if (!Array.isArray(old)) return old
      return old.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c))
    },
  )
}

// ─── Messages (Infinite Query + Realtime) ───────────────────────────────────

export function useMessages(conversationId: string | undefined) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const userId = user?.id ?? ''

  const query = useInfiniteQuery({
    queryKey: messageKeys.list(conversationId ?? ''),
    queryFn: ({ pageParam }) =>
      messagingService.getMessages(conversationId!, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: MessagePage) => lastPage.nextCursor ?? undefined,
    enabled: !!conversationId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000, // Keep cached messages for 5 min after unmount
  })

  // Mark as read on mount + clear unread badge in conversation list.
  // Reads through use-conversations' targeted unread bump (see that file)
  // so a fast tap into a conversation doesn't trigger a full refetch.
  useEffect(() => {
    if (conversationId && userId) {
      void messagingService.markAsRead(conversationId, userId).then(() => {
        clearConversationUnread(queryClient, userId, conversationId)
      })
    }
  }, [conversationId, userId, queryClient])

  // Realtime: prepend new messages, OR upgrade an optimistic message in
  // place when the server echoes it back via the postgres_changes payload.
  useEffect(() => {
    if (!conversationId) return

    // Per-subscription flag: the first SUBSCRIBED is the initial connect; any
    // later one is a reconnect, and the gap it left has to be closed by hand.
    let hasSubscribedOnce = false

    const channel = supabase
      .channel(`conv:${conversationId}:messages`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const row = payload.new as Record<string, unknown>

          // Build a partial MessageItem from the realtime row. The payload
          // does not include the joined `profiles.display_name/avatar_url`
          // columns — we fill those from the conversation list cache (the
          // other participant's profile is already resolved there).
          const senderId = row.sender_id as string
          const isOwn = senderId === userId
          const replyToId = (row.reply_to_id as string | null | undefined) ?? null
          const queryKey = messageKeys.list(conversationId)
          const cachedReply = findCachedReplyPreview(
            queryClient.getQueryData<MessagesQueryData>(queryKey),
            replyToId,
          )

          let senderDisplayName: string | null = null
          let senderAvatarUrl: string | null = null

          if (isOwn) {
            senderDisplayName = (user?.user_metadata?.display_name as string | undefined) ?? null
            senderAvatarUrl = (user?.user_metadata?.avatar_url as string | undefined) ?? null
          } else {
            // Look the sender up in the conversation cache (already loaded
            // by use-conversations). If absent the next refetch will fill
            // in the gap; we use senderId as the visible label fallback so
            // the bubble doesn't render an obvious empty state.
            const conversations = queryClient.getQueryData<
              {
                id: string
                otherUserId: string
                otherUserDisplayName: string | null
                otherUserAvatarUrl: string | null
              }[]
            >(conversationKeys.list(userId))
            const conv = conversations?.find((c) => c.id === conversationId)
            if (conv && conv.otherUserId === senderId) {
              senderDisplayName = conv.otherUserDisplayName
              senderAvatarUrl = conv.otherUserAvatarUrl
            }
          }

          const incoming: MessageItem = {
            id: row.id as string,
            conversationId: row.conversation_id as string,
            senderId,
            senderDisplayName,
            senderAvatarUrl,
            content: (row.content as string | null) ?? null,
            messageType: (row.message_type as MessageItem['messageType']) ?? 'text',
            mediaUrl: (row.media_url as string | null) ?? null,
            mediaBlurhash: (row.media_blurhash as string | null) ?? null,
            locationLat: (row.location_lat as number | null) ?? null,
            locationLng: (row.location_lng as number | null) ?? null,
            isEdited: row.is_edited === true,
            isDeleted: row.is_deleted === true,
            replyToId,
            replyTo: cachedReply,
            createdAt: row.created_at as string,
            clientMessageId: (row.client_message_id as string | null) ?? null,
            detectedTypes: (row.detected_types as string[] | undefined) ?? [],
          }

          queryClient.setQueryData<MessagesQueryData>(queryKey, (old) =>
            reconcileMessage(old, incoming, 'realtime'),
          )

          // The parent can be older than the loaded pages. Hydrate it in one
          // authorized lookup so an incoming reply never degrades into an
          // unexplained standalone message; a transient failure repairs by
          // refetching the canonical page instead of being silently ignored.
          if (replyToId && !cachedReply) {
            void messagingService
              .getReplyPreview(replyToId)
              .then((preview) => {
                if (!preview) return
                queryClient.setQueryData<MessagesQueryData>(queryKey, (old) =>
                  hydrateReplyPreview(old, incoming.id, preview),
                )
              })
              .catch(() => {
                void queryClient.invalidateQueries({ queryKey })
              })
          }

          // Auto-mark-read because the user is looking at this thread right
          // now. Skip for own messages — they're already read. Clearing the
          // conversation-list row too (not just the tab total) is what stops
          // the badge lighting up for a message on screen.
          if (!isOwn) {
            void messagingService.markAsRead(conversationId, userId).then(() => {
              clearConversationUnread(queryClient, userId, conversationId)
            })
          }
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        () => {
          // Edits through the API also pass the server filter. Refresh their
          // canonical content and flags, including edits to older messages.
          queryClient.invalidateQueries({ queryKey: messageKeys.list(conversationId) })
        },
      )
      .subscribe((status) => {
        if (status !== 'SUBSCRIBED') return
        // A socket that drops mid-conversation misses every INSERT while it is
        // down. The client reconnects on its own, but nothing replayes the gap:
        // the thread silently skipped messages until the next manual refresh.
        // On every RE-established subscription (not the first), refetch the
        // newest page so the gap closes by itself.
        if (hasSubscribedOnce) {
          void queryClient.invalidateQueries({ queryKey: messageKeys.list(conversationId) })
        }
        hasSubscribedOnce = true
      })

    return () => {
      void supabase.removeChannel(channel)
    }
  }, [conversationId, userId, user, queryClient])

  // Flatten pages into a single message array
  const messages = query.data?.pages.flatMap((p) => p.messages) ?? []

  return {
    messages,
    isLoading: query.isLoading,
    error: query.error,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    refetch: query.refetch,
  }
}

// ─── Conversation Search ───────────────────────────────────────────────────

/**
 * Search is intentionally a separate cache from the rendered timeline. A
 * result can be older than the pages on screen, so filtering `messages` would
 * lie by silently omitting history the user is entitled to search.
 */
export function useConversationMessageSearch(conversationId: string | undefined, query: string) {
  const normalized = query.trim()
  const search = useQuery({
    queryKey: [...messageKeys.list(conversationId ?? ''), 'search', normalized],
    queryFn: () => messagingService.searchConversationMessages(conversationId!, normalized),
    enabled: Boolean(conversationId) && normalized.length >= 2,
    staleTime: 30 * 1000,
  })

  return {
    results: (search.data ?? []) as MessageSearchResult[],
    isLoading: search.isLoading,
    isFetching: search.isFetching,
    error: search.error,
    refetch: search.refetch,
  }
}

// ─── Send Message (Optimistic) ──────────────────────────────────────────────

export function useSendMessage(conversationId: string) {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const userId = user?.id ?? ''

  const mutation = useMutation({
    mutationFn: (input: Omit<SendMessageInput, 'conversationId'>) =>
      messagingService.sendMessage(userId, { ...input, conversationId }),

    onMutate: async (input) => {
      const key = messageKeys.list(conversationId)
      await queryClient.cancelQueries({ queryKey: key })

      const previous = queryClient.getQueryData<MessagesQueryData>(key)

      // Filter client-side so optimistic UI matches server response.
      const filteredContent = input.content ? filterMessage(input.content).filtered : null

      // Pre-generate the dedupe key so the realtime echo can find this
      // optimistic row and upgrade it in place. We piggy-back on the same
      // UUID for the optimistic id (so React's keyExtractor stays stable
      // when the row gets swapped by reconcileMessage).
      const clientMessageId = input.clientMessageId ?? Crypto.randomUUID()

      const optimistic: MessageItem = {
        id: `optimistic-${clientMessageId}`,
        conversationId,
        senderId: userId,
        senderDisplayName: (user?.user_metadata?.display_name as string | undefined) ?? null,
        senderAvatarUrl: (user?.user_metadata?.avatar_url as string | undefined) ?? null,
        content: filteredContent,
        messageType: input.messageType ?? 'text',
        mediaUrl: input.mediaUrl ?? null,
        mediaBlurhash: input.mediaBlurhash ?? null,
        locationLat: input.locationLat ?? null,
        locationLng: input.locationLng ?? null,
        isEdited: false,
        isDeleted: false,
        replyToId: input.replyToId ?? null,
        replyTo: input.replyTo ?? null,
        createdAt: new Date().toISOString(),
        clientMessageId,
      }

      queryClient.setQueryData<MessagesQueryData>(key, (old) => {
        if (!old || !old.pages[0]) {
          return {
            pages: [{ messages: [optimistic], nextCursor: null }],
            pageParams: [undefined],
          }
        }
        const firstPage = old.pages[0]
        return {
          ...old,
          pages: [
            { ...firstPage, messages: [optimistic, ...firstPage.messages] },
            ...old.pages.slice(1),
          ],
        }
      })

      // Mutate the input so the service writes the same id we attached
      // locally. The mutation runs with the modified input (TanStack
      // re-uses the original variables object).
      input.clientMessageId = clientMessageId

      return { previous, clientMessageId }
    },

    onError: (_err, _input, context) => {
      // Keep the bubble. A failed send used to be reverted away and handed
      // back as composer text plus a toast: the message left the conversation,
      // so anyone who missed the toast believed it was sent. The row now stays,
      // marked, and the bubble owns the retry.
      if (!context?.clientMessageId) {
        if (context?.previous) {
          queryClient.setQueryData(messageKeys.list(conversationId), context.previous)
        }
        return
      }
      queryClient.setQueryData<MessagesQueryData>(messageKeys.list(conversationId), (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            messages: page.messages.map((message) =>
              message.clientMessageId === context.clientMessageId
                ? { ...message, sendFailed: true }
                : message,
            ),
          })),
        }
      })
    },

    onSuccess: (message) => {
      // Canonicalize even if realtime is late or disconnected: both the
      // redacted content and its detected types come from the stored row.
      queryClient.setQueryData<MessagesQueryData>(messageKeys.list(conversationId), (old) =>
        reconcileMessage(old, message),
      )
    },

    // The response/echo already reconciled the message. Refetch only the
    // conversation list for unread + lastMessageAt bookkeeping.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.all })
    },
  })

  /**
   * Re-send a failed optimistic row.
   *
   * The failed row is dropped first: the mutation's optimistic insert reuses
   * the same `optimistic-<clientMessageId>` id, and keeping both would put two
   * rows under one React key. A second failure is handled by the same
   * `onError`, which re-marks the new row. Returns false instead of throwing so
   * the caller can report and stay out of the way.
   */
  const retryMessage = useCallback(
    async (message: MessageItem): Promise<boolean> => {
      const clientMessageId = message.clientMessageId
      if (!clientMessageId) return false

      queryClient.setQueryData<MessagesQueryData>(messageKeys.list(conversationId), (old) => {
        if (!old) return old
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            messages: page.messages.filter((m) => m.clientMessageId !== clientMessageId),
          })),
        }
      })

      try {
        await mutation.mutateAsync({
          content: message.content ?? undefined,
          messageType: message.messageType,
          mediaUrl: message.mediaUrl ?? undefined,
          mediaBlurhash: message.mediaBlurhash ?? undefined,
          locationLat: message.locationLat ?? undefined,
          locationLng: message.locationLng ?? undefined,
          replyToId: message.replyToId,
          replyTo: message.replyTo,
          clientMessageId,
        })
        return true
      } catch {
        return false
      }
    },
    [conversationId, mutation, queryClient],
  )

  return {
    sendMessage: mutation.mutateAsync,
    retryMessage,
    isPending: mutation.isPending,
    error: mutation.error,
  }
}
